import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Download as DownloadIcon, Upload, Users, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// Standalone page for bulk-uploading per-client reporting preferences.
// Linked from Masters → Profile → "Report Preference Upload".
const BulkClientPrefsUpload = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/reporting-preferences/clients-summary")
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null));
  }, [api]);

  const refreshSummary = async () => {
    try {
      const r = await api.get("/reporting-preferences/clients-summary");
      setSummary(r.data);
    } catch (_e) { /* ignore */ }
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const baseUrl = api.defaults.baseURL;
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/reporting-preferences/clients-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `client_reporting_preferences_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e?.message || "Failed to download template");
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/reporting-preferences/clients-bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { rows_processed, updated, errors, errors_truncated } = r.data;
      if (errors && errors.length) {
        toast.warning(`${updated} updated · ${errors.length} warnings${errors_truncated ? "+" : ""}`, {
          description: errors.slice(0, 3).join(" · "),
        });
      } else {
        toast.success(`${updated} client preferences saved (of ${rows_processed} rows)`);
      }
      refreshSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl" data-testid="bulk-client-prefs-page">
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate("/masters")}
          data-testid="bulk-prefs-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Masters
        </Button>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Report Preference Upload</h1>
      </div>
      <p className="text-sm text-gray-600">
        Bulk-update reporting preferences for every client at once. Download the template (pre-filled with each PAN's current settings), edit Y/N flags / frequency / email per row in Excel, then re-upload.
      </p>

      <Card className="border-gray-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="w-4 h-4 text-teal-700" />
            Current state
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {summary ? (
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg bg-gray-50 px-3 py-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500">Clients with prefs</p>
                <p className="text-lg font-semibold text-gray-900 mt-0.5">{summary.total_with_prefs.toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-green-50 px-3 py-3">
                <p className="text-[10px] uppercase tracking-wider text-green-700">Receiving reports</p>
                <p className="text-lg font-semibold text-green-900 mt-0.5">{summary.enabled.toLocaleString()}</p>
              </div>
              <div className="rounded-lg bg-amber-50 px-3 py-3">
                <p className="text-[10px] uppercase tracking-wider text-amber-700">Opted out</p>
                <p className="text-lg font-semibold text-amber-900 mt-0.5">{summary.opted_out.toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Loading current state…</p>
          )}
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              disabled={downloading}
              className="gap-2"
              data-testid="bulk-prefs-download"
            >
              {downloading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <DownloadIcon className="w-4 h-4" />}
              Download template
            </Button>
            <label className="inline-flex">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleUpload}
                className="hidden"
                data-testid="bulk-prefs-upload-input"
              />
              <span
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer ${
                  uploading
                    ? "bg-gray-100 text-gray-500"
                    : "bg-teal-700 text-white hover:bg-teal-800"
                }`}
              >
                {uploading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Upload className="w-4 h-4" />}
                Upload edited Excel
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-500 pt-1">
            Y / N grammar accepted: <span className="font-mono">Y, YES, TRUE, 1</span> for ON · anything else is OFF. PANs missing from the upload keep their previous prefs.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BulkClientPrefsUpload;
